<script setup lang="ts">
import { computed } from 'vue'
import * as formatNumber from '@/helpers/numberFormatters'
import type { LoginSectionProps, SmartbookGrantContext } from '../useLoginSection'

const props = defineProps<LoginSectionProps & {
  currentSection: string
  grantMode: boolean
  context: SmartbookGrantContext
}>()

const smartbookInfo = computed(() => {
  const base = props.context.smartbook
  const tariff = props.query.get('smartbook_tariff') || 'b'
  const price = { b: base?.basic_price, e: base?.extended_price }[tariff] || 0
  const tariffString = { b: 'Стандартная версия', e: 'Расширенная версия' }[tariff] || ''
  const accessMonths = { b: '18 месяцев', e: '24 месяца' }[tariff] || ''
  return {
    price,
    tariffString,
    accessMonths,
    course_name: base?.course_name,
    course_id: base?.course_id
  }
})

const purchaseInfo = computed(() => {
  const quantity = props.sharedData.smartbookStudents?.length || 1
  return { amount: (smartbookInfo.value.price || 0) * quantity, quantity }
})

const currentStep = computed(() => {
  if (props.grantMode) {
    switch (props.currentSection) {
      case props.LoginSection.SmartbookStudentSelect:
      case props.LoginSection.StudentCreate:
        return 1
      default:
        return 0
    }
  }

  switch (props.currentSection) {
    case props.LoginSection.SignupProfileForm:
      return 0
    case props.LoginSection.EmailVerification:
      return 1
    case props.LoginSection.SmartbookStudentSelect:
    case props.LoginSection.StudentCreate:
      return 2
    case props.LoginSection.SmartbookPurchase:
      return 3
    default:
      return 0
  }
})

function formatPrice(val?: number) {
  if (!val) return '—'
  return formatNumber.price.rub(val)
}
</script>

<template>
  <div class="login-smartbook-sidebar">
    <span v-if="grantMode" class="login__aside-text"> Получить Умный Учебник </span>
    <span v-else class="login__aside-text"> Покупка Умного Учебника </span>

    <div class="login-smartbook-sidebar__card pa-3 mt-4">
      <div class="d-flex text-title-bold">
        <span class="flex-spacer mr-2"> Итого: </span>
        <span>
          {{ formatPrice(purchaseInfo.amount) }}

          <span v-if="grantMode" class="login-smartbook-sidebar__paid-badge">
            <v-icon class="mr-half" size="16">mdi-check</v-icon>
            Оплачено
          </span>
        </span>
      </div>

      <div class="d-flex mt-1">
        <span class="flex-spacer mr-2 text-body-bold"> «Умный Учебник» {{ smartbookInfo.course_name }} </span>
        <span> Количество: {{ purchaseInfo.quantity }} </span>
      </div>

      <div class="mt-1">{{ smartbookInfo.tariffString }} – Доступ на {{ smartbookInfo.accessMonths }} мес.</div>
    </div>

    <ol v-if="grantMode" class="login-smartbook-sidebar__card login-smartbook-sidebar__stepper mt-2 pa-2">
      <li :active="currentStep > 0">
        <p class="text-body-bold">Заполните профиль</p>
        <p class="gray-text mt-1">Укажите ваши данные родителя</p>
      </li>

      <li :active="currentStep > 1">
        <p class="text-body-bold">Выбор ученика</p>
        <p class="gray-text mt-1">
          Умный Учебник приобретается индивидуально для ученика. Выберите ученика или добавьте нового.
        </p>
      </li>
    </ol>

    <ol v-else class="login-smartbook-sidebar__card login-smartbook-sidebar__stepper mt-2 pa-2">
      <li :active="currentStep > 0">
        <p class="text-body-bold">Регистрация</p>
        <p class="gray-text mt-1">Войдите на сайт или создайте аккаунт родителя</p>
      </li>

      <li :active="currentStep > 1">
        <p class="text-body-bold">Подтверждение почты</p>
        <p class="gray-text mt-1">Введите код подтверждения, отправленный на вашу почту</p>
      </li>

      <li :active="currentStep > 2">
        <p class="text-body-bold">Добавить ученика</p>
        <p class="gray-text mt-1">Умный Учебник приобретается индивидуально для ученика</p>
      </li>

      <li>
        <p class="text-body-bold">Оплата</p>
        <p class="gray-text mt-1">
          После оплаты учебник будет доступен в кабинете ученика онлайн на сайте МирГоворит через ваш браузер
        </p>
      </li>
    </ol>
  </div>
</template>

<style lang="scss">
.login-smartbook-sidebar {
  &__card {
    border-radius: 8px;
    background: white;
    box-shadow:
      0px 8px 16px 0px rgba(black, 0.05),
      0px 2px 8px rgba(black, 0.1);

    max-width: 550px;
  }

  &__paid-badge {
    background: $green;
    color: white;
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    line-height: 16px;
    font-weight: bold;
    border-radius: 4px;
    padding: 4px 8px;
    margin-left: 16px;
  }

  &__stepper {
    & > li {
      display: list-item;
      margin-left: 32px;
      position: relative;
      padding: 16px;

      &[active] + li:not([active]),
      &:not([active]):first-child {
        background: rgba($blue, 0.05);
        border-radius: 8px;

        &::before {
          background: rgba($blue, 0.3);
          color: $blue;
        }
      }

      &[active] {
        &::before {
          background: rgba($green, 0.3);
          color: $green;
          content: '✓';
        }
      }

      & + li {
        margin-top: 8px;
      }

      &::before {
        left: -32px;
        position: absolute;

        content: counters(list-item, '.');
        width: 24px;
        height: 24px;
        display: inline-block;
        text-align: center;
        border-radius: 4px;

        background: rgba($gray, 0.3);
        color: $gray;
      }

      &:not(:last-child)::after {
        content: ' ';
        display: block;
        width: 1.5px;
        height: calc(100% - 28px);
        background: $lightGray;
        position: absolute;
        left: -20px;
        top: 47px;
        border-radius: 4px;
      }
    }
  }
}
</style>
